/**
 * Client-side image compression for uploads.
 *
 * Modern phone cameras (12MP+) routinely produce 5–8 MB JPEGs, which
 * exceeded our 3MB upload cap on ClubVIP selfies. Instead of asking users
 * to shrink photos themselves, we transparently resize + re-encode any
 * oversized image in the browser before it hits the network.
 *
 * Strategy:
 *  1. Load into <img> via createObjectURL (fast, no data-url parsing).
 *  2. Draw onto a canvas with the longest side clamped to `maxDim` px.
 *  3. Re-encode as JPEG at quality 0.85; if still larger than
 *     `targetBytes`, iteratively drop quality until it fits (or bottom
 *     out at 0.45, whichever is smaller).
 *  4. Return a File so downstream FormData code doesn't change.
 *
 * PNG/WebP inputs are re-encoded to JPEG (transparency isn't needed for
 * profile photos and JPEG is ~30% smaller for the same quality).
 */

const DEFAULT_MAX_DIM = 1600;      // long edge — 1600 keeps a phone selfie sharp on any screen
const DEFAULT_TARGET_BYTES = 1.5 * 1024 * 1024;  // aim well under our 3MB cap
const MIN_QUALITY = 0.45;

function loadImage(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = (e) => {
      URL.revokeObjectURL(url);
      reject(e);
    };
    img.src = url;
  });
}

function canvasToBlob(canvas, mime, quality) {
  return new Promise((resolve) => canvas.toBlob(resolve, mime, quality));
}

/**
 * Compress an image File into a smaller JPEG File.
 * Returns the original File unchanged if it already meets the target
 * or if compression fails (defensive: never block an upload).
 */
export async function compressImage(file, {
  maxDim = DEFAULT_MAX_DIM,
  targetBytes = DEFAULT_TARGET_BYTES,
} = {}) {
  if (!file || !file.type?.startsWith("image/")) return file;
  // Small images already fit — skip work.
  if (file.size <= targetBytes && file.type === "image/jpeg") return file;

  try {
    const img = await loadImage(file);
    let { width, height } = img;
    const longest = Math.max(width, height);
    if (longest > maxDim) {
      const scale = maxDim / longest;
      width = Math.round(width * scale);
      height = Math.round(height * scale);
    }

    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(img, 0, 0, width, height);

    let quality = 0.85;
    let blob = await canvasToBlob(canvas, "image/jpeg", quality);
    while (blob && blob.size > targetBytes && quality > MIN_QUALITY) {
      quality = Math.max(MIN_QUALITY, quality - 0.1);
      blob = await canvasToBlob(canvas, "image/jpeg", quality);
    }

    if (!blob) return file;
    // If compression somehow made it larger (rare, e.g. tiny GIF), keep the original.
    if (blob.size >= file.size && file.type === "image/jpeg") return file;

    const baseName = (file.name || "upload").replace(/\.[^.]+$/, "");
    return new File([blob], `${baseName}.jpg`, {
      type: "image/jpeg",
      lastModified: Date.now(),
    });
  } catch {
    return file;
  }
}

export default compressImage;
